/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { CanvasComponent } from '@opengeoweb/shared';
import type { LayerFoundation } from '@opengeoweb/webmap';
import {
  makeStacLegendModel,
  getStacAssetKeyFromStyle,
  getStacLegendWidth,
  getStacLegendMetadata,
  renderStacLegend,
  useStacCollection,
} from '../../geotiff-layer-api';

interface StacLegendCanvasProps {
  layer: Pick<LayerFoundation, 'service' | 'id' | 'style'>;
  height: number;
  width: number;
  opacity: number;
  onHeightChange: (height: number) => void;
  onWidthChange?: (width: number) => void;
}

export const StacLegendCanvas: React.FC<StacLegendCanvasProps> = ({
  layer,
  height,
  width,
  opacity,
  onHeightChange,
  onWidthChange,
}) => {
  const collection = useStacCollection(layer.service ?? '', layer.id, true);
  const renders = collection.data?.renders;
  const selectedAssetKey = getStacAssetKeyFromStyle(layer.style);
  const collectionRenderEntry =
    renders?.[layer.style ?? ''] ??
    renders?.default ??
    Object.values(renders ?? {})[0];
  const variable = Object.values(collection.data?.['cube:variables'] ?? {})[0];

  return (
    <CanvasComponent
      onRenderCanvas={(ctx): void => {
        // Read at draw time: the active item changes with time and is picked up by the redraw interval.
        const activeMetadata = getStacLegendMetadata(layer.id);
        const legendAssetKey =
          selectedAssetKey ??
          (collectionRenderEntry ? undefined : activeMetadata?.assetKey);
        const renderEntry = legendAssetKey
          ? {
              assets: [legendAssetKey],
              title:
                collection.data?.item_assets?.[legendAssetKey]?.title ??
                legendAssetKey,
            }
          : collectionRenderEntry;
        const band =
          activeMetadata?.band ??
          collection.data?.item_assets?.[renderEntry?.assets[0] ?? '']?.[
            'raster:bands'
          ]?.[0] ??
          collection.data?.summaries?.['raster:bands']?.[0];

        if (!renderEntry) {
          // eslint-disable-next-line no-param-reassign
          ctx.fillStyle = '#fff';
          ctx.fillRect(0, 0, width, height);
          return;
        }

        const model = makeStacLegendModel({ renderEntry, variable, band });
        onWidthChange?.(getStacLegendWidth(model, ctx));
        onHeightChange(renderStacLegend(model, ctx, opacity));
      }}
      redrawInterval={500}
    />
  );
};
